import { useRef, useState } from "react";
import { Mail, Phone, Globe, Download, Loader2 } from "lucide-react";
import Logo from "@/components/Logo";
import {
  CONTACT_INFO,
  STATEMENT_CERTS,
  STATEMENT_COMPETENCIES,
  CAPABILITIES,
} from "@/data/content";

const SectionHeading = ({ children }) => (
  <div className="flex items-center gap-3 mb-4">
    <span className="w-1 h-5 bg-[#0055FF] shrink-0" />
    <h2 className="font-heading font-bold uppercase tracking-wide text-lg text-white">{children}</h2>
  </div>
);

export default function CapabilityStatement() {
  const contentRef = useRef(null);
  const [downloading, setDownloading] = useState(false);

  const handleDownload = async () => {
    if (!contentRef.current) return;
    setDownloading(true);
    try {
      const html2pdf = (await import("html2pdf.js")).default;
      const el = contentRef.current;
      const w = Math.ceil(el.scrollWidth);
      const h = Math.ceil(el.scrollHeight) + 4;
      const opt = {
        margin: 0,
        filename: "Sabala-Management-Capability-Statement.pdf",
        image: { type: "jpeg", quality: 0.98 },
        html2canvas: {
          scale: 2,
          backgroundColor: "#0A0A0A",
          useCORS: true,
          windowWidth: w,
          onclone: (doc) => {
            doc.documentElement.style.backgroundColor = "#0A0A0A";
            doc.body.style.backgroundColor = "#0A0A0A";
          },
        },
        jsPDF: {
          unit: "px",
          format: [w, h],
          orientation: "portrait",
          hotfixes: ["px_scaling"],
        },
        pagebreak: { mode: ["avoid-all"] },
      };
      await html2pdf().set(opt).from(el).save();
    } finally {
      setDownloading(false);
    }
  };

  const info = [
    { label: "DUNS", value: CONTACT_INFO.duns },
    { label: "CAGE", value: CONTACT_INFO.cage },
    { label: "Unique ID", value: CONTACT_INFO.uei },
    { label: "GSA", value: CONTACT_INFO.gsa },
  ];

  return (
    <div data-testid="capability-statement" className="min-h-screen bg-[#0A0A0A] text-white">
      <button
        onClick={handleDownload}
        disabled={downloading}
        data-testid="download-statement-button"
        className="fixed top-5 right-5 z-50 inline-flex items-center gap-2 px-5 py-3 bg-[#0055FF] hover:bg-[#0044CC] disabled:opacity-60 text-white font-bold uppercase tracking-wide text-xs sm:text-sm shadow-lg transition-transform hover:scale-[1.04]"
      >
        {downloading ? <Loader2 size={15} className="animate-spin" /> : <Download size={15} />}
        {downloading ? "Preparing…" : "Download PDF"}
      </button>

      <div
        ref={contentRef}
        className="max-w-[1050px] mx-auto px-6 sm:px-10 py-12 text-white"
        style={{ backgroundColor: "#0A0A0A" }}
      >
        {/* Top row: badges + logo */}
        <div className="flex items-start justify-between gap-6 flex-wrap">
          <div className="flex gap-3">
            {["WOSB", "8(a)"].map((b) => (
              <div
                key={b}
                className="bg-[#0055FF] px-5 py-2.5 text-center leading-tight"
                data-testid={`badge-${b}`}
              >
                <p className="font-heading font-black text-lg tracking-tight">{b}</p>
                <p className="font-mono text-[9px] uppercase tracking-[0.2em] text-white/85">Certified</p>
              </div>
            ))}
          </div>
          <div className="bg-[#0055FF] px-10 py-6 flex items-center justify-center">
            <Logo className="text-[34px] text-white" />
          </div>
        </div>

        {/* Title */}
        <h1 className="font-heading font-black uppercase tracking-tighter text-5xl sm:text-6xl md:text-7xl leading-[0.92] mt-8">
          Sabala Management LLC
        </h1>
        <div className="h-[2px] bg-[#0055FF] mt-6" />

        {/* Two columns */}
        <div className="grid md:grid-cols-[290px_1fr] gap-10 lg:gap-16 mt-10">
          {/* Left column */}
          <aside className="space-y-10">
            <div>
              <p className="font-heading font-bold uppercase tracking-wide text-blue-400 text-sm mb-4">
                Company Information
              </p>
              <ul className="space-y-2 text-sm">
                {info.map((r) => (
                  <li key={r.label}>
                    <span className="font-bold text-white">{r.label}:</span>{" "}
                    <span className="text-white/70 font-mono">{r.value}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div>
              <p className="font-heading font-bold uppercase tracking-wide text-blue-400 text-sm mb-4">
                Federal Certifications &amp; Compliance
              </p>
              <ul className="space-y-2 text-sm">
                {STATEMENT_CERTS.map((c) => (
                  <li key={c} className="flex items-start gap-2 text-white/75">
                    <span className="text-blue-500 mt-1.5 text-[8px]">●</span>
                    {c}
                  </li>
                ))}
              </ul>
            </div>

            <div>
              <p className="font-heading font-bold uppercase tracking-wide text-blue-400 text-sm mb-4">
                Core Competencies
              </p>
              <ul className="space-y-2 text-sm">
                {STATEMENT_COMPETENCIES.map((c) => (
                  <li key={c} className="flex items-start gap-2 text-white/75">
                    <span className="text-blue-500 mt-1.5 text-[8px]">●</span>
                    {c}
                  </li>
                ))}
              </ul>
            </div>

            <div className="pt-2 text-sm">
              <p className="font-bold text-white mb-1">Sabala Management LLC</p>
              <p className="text-white/70">961 North Shadowridge Ave.</p>
              <p className="text-white/70">Eagle, Idaho 83616</p>
            </div>
          </aside>

          {/* Right column */}
          <div className="space-y-8">
            {CAPABILITIES.map((c) => (
              <div key={c.title}>
                <SectionHeading>{c.title}</SectionHeading>
                {c.body.split(/(?<=\.)\s+(?=Our team delivers)/).map((para, idx) => (
                  <p key={idx} className="text-white/65 text-sm leading-relaxed font-light mb-3 last:mb-0">
                    {para}
                  </p>
                ))}
              </div>
            ))}
          </div>
        </div>

        {/* Divider */}
        <div className="h-[2px] bg-[#0055FF] mt-12" />

        {/* Contact cards */}
        <div className="grid sm:grid-cols-2 gap-6 mt-10">
          {CONTACT_INFO.people.map((p) => (
            <div
              key={p.email}
              data-testid={`statement-contact-${p.name.split(" ")[0].toLowerCase()}`}
              className="border border-white/10 bg-[#141414] p-6"
            >
              <p className="font-heading font-bold text-2xl tracking-tight" style={{ fontFamily: '"Playfair Display", serif' }}>
                {p.name}
              </p>
              <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-blue-400 mt-1 mb-4">{p.title}</p>
              <ul className="space-y-2 text-sm text-white/75">
                <li className="flex items-center gap-2">
                  <Mail size={13} className="text-blue-500" /> {p.email}
                </li>
                <li className="flex items-center gap-2">
                  <Phone size={13} className="text-blue-500" /> {p.phone}
                </li>
                <li className="flex items-center gap-2">
                  <Globe size={13} className="text-blue-500" /> www.sabalamgmt.com
                </li>
              </ul>
              <div className="mt-4 pt-3 border-t border-white/10 text-xs text-white/45 space-y-1">
                <p>Women Owned Small Business ~ 8(a)</p>
                <p>GSA Contract Number: {CONTACT_INFO.gsa}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
